<template>
    <div class="d-flex flex-column pa-2 fill-height">
        <v-card class="d-flex flex-column pa-2 fill-height card-extra-space">
            <v-card-title class="title-remove-space">
                Biosecurity Incidents
            </v-card-title>
            <div class="d-flex mb-4">
                <v-form v-model="isFormValid">
                    <date-field class="d-inline-flex mr-2" label="From" v-model="from" required />
                    <date-field class="d-inline-flex mr-2" label="To" v-model="to" required />
                </v-form>
                <div class="mt-3">
                    <v-btn class="mr-2" v-on:click="back"><v-icon>mdi-arrow-left-bold</v-icon></v-btn>
                    <v-btn class="mr-2" v-on:click="thisMonth"><v-icon>mdi-circle-medium</v-icon></v-btn>
                    <v-btn class="mr-2" v-on:click="forward"><v-icon>mdi-arrow-right-bold</v-icon></v-btn>
                </div>
                <div class="flex-grow-1"></div>
                <v-dialog v-model="dialog" max-width="500px" persistent>
                    <template v-slot:activator="{ on }">
                        <v-btn v-if="canEdit('BiosecurityIncidents')" color="primary" dark v-on="on" class="float-right" @click="addItem">Add Incident</v-btn>
                    </template>

                    <biosecurity-incident-form :filter="editedFilter" v-model="editedItem" @open="dialog = true" @input="onSave" @delete="onDelete" @cancel="onCancel" />
                </v-dialog>
                <v-btn color="quaternary" dark class="ml-2 float-right" v-on:click="download">Export to Excel</v-btn>
            </div>
            <div class="flex-grow-1 d-flex">
                <div class="d-flex flex-column">
                    <div class="flex-scroll">
                        <v-text-field v-model="search" append-icon="mdi-search" label="Search" single-line hide-details />
                        <v-data-table class="hover-cell card-min-height" :headers="filteredHeaders" :items="computedItems" @click:row="rowClicked" :loading="isLoading" item-key="id" dense :hide-default-footer="true" mobile-breakpoint="4000">
                            <template v-slot:[`item.on`]="{ item }">
                                {{ formatDate(item.on) }}
                            </template>
                            <template v-slot:[`item.memberId`]="{ item }">
                                {{ formatLongString(members[item.memberId], 30) }}
                            </template>
                            <template v-slot:[`item.contactId`]="{ item }">
                                {{ formatLongString(contacts[item.contactId], 30) }}
                            </template>
                            <template v-slot:[`item.coordinates`]="{ item }">
                                {{ formatCoordinates(item.coordinates) }}
                            </template>
                            <template v-slot:[`item.afflictionId`]="{ item }">
                                {{ formatLongString(afflictions[item.afflictionId], 30) }}
                            </template>
                            <template v-slot:[`item.inspectionId`]="{ item }">
                                <v-btn @click="goToInspection(item.inspectionId)">{{ formatLongString(inspections[item.inspectionId], 30) }}</v-btn>
                            </template>
                            <template v-slot:[`item.comments`]="{ item }">
                                {{ formatLongString(item.comments, 30) }}
                            </template>
                            <template v-if="canRead('BiosecurityIncidents')" v-slot:[`item.action`]="{ item }">
                                <div class="d-flex flex-nowrap justify-end">
                                    <v-icon v-if="canEdit('BiosecurityIncidents')" class="mr-2" @click="editItem(item)">mdi-pencil</v-icon>
                                </div>
                            </template>
                        </v-data-table>
                    </div>
                </div>
                <div id="incident-map" class="flex-grow-1" />
            </div>

            <no-data-dialog v-if="notifyDialog" @input="notifyDialog = false" />
        </v-card>
    </div>
</template>

<script>
    import axios from 'axios'
    import { DateTime } from 'luxon'
    import exporter from '../export/exporter'
    import securityService from '../services/securityService'
    import dateUtility from '../services/dateUtility'

    export default {
        props: [
            'filter'
        ],
        components: {
            noDataDialog: () => import('../export/NoDataDialog'),
            biosecurityIncidentForm: () => import('../forms/BiosecurityIncidentForm'),
            dateField: () => import('../components/DateField')
        },
        data() {
            return {
                editedFilter: null,
                isLoading: false,
                search: '',
                headers: [
                    { text: 'On', value: 'on' },
                    { text: 'Member', value: 'memberId' },
                    { text: 'Coordinates', value: 'coordinates' },
                    { text: 'Pest/Disease', value: 'afflictionId' },
                    { text: 'Inspection', value: 'inspectionId' },
                    { text: 'Comments', value: 'comments' },
                    { text: '', value: 'action' }
                ],
                items: [],
                contacts: [],
                members: [],
                afflictions: [],
                inspections: [],

                dialog: false,
                notifyDialog: false,
                editedItem: {},

                from: null,
                to: null,
                highlightButton: null,
                isFormValid: true,
                updatingFilter: false,
                click: 0,
                mainFilter: {},

                map: null,
                infoWindow: null,
                browserLocation: null,
                google: null
            }
        },
        mounted() {
            this.mainFilter = { ...this.filter };
            this.thisMonth();
            var memberId = securityService.getMemberId();
            if (memberId) {
                this.editedFilter = { ...this.filter, memberId: memberId };
            }

            this.dialog = false;
            this.google = window.google
            this.browserLocation = new this.google.maps.LatLng(-31.95, 115.86)
        },
        methods: {
            async getBiosecurityIncidents(params) {
                return await axios.get('/api/BiosecurityIncidents', { params });
            },
            async load() {
                this.click++;
                var currentClick = this.click;
                if (!this.isLoading) {
                    var params = {};
                    params.sortBy = "on";
                    params.direction = "descending";
                    if (typeof this.mainFilter !== 'undefined' && this.mainFilter !== null) {
                        Object.keys(this.mainFilter).forEach(key => params['filter.' + key] = this.mainFilter[key]);
                    }
                    var response = await this.runAsyncCommand(this, 'isLoading', this.getBiosecurityIncidents, params);
                    var data = response.data;
                    if (this.click !== currentClick) {
                        await this.load();
                    }

                    this.contacts = data.contacts;
                    this.members = data.members;
                    this.afflictions = data.afflictions;
                    this.inspections = data.inspections;
                    this.items = data.items;
                }
            },

            loadMap() {
                if (navigator.geolocation) {
                    var self = this;
                    navigator.geolocation.getCurrentPosition(position => {
                        self.browserLocation = new this.google.maps.LatLng(position.coords.latitude, position.coords.longitude);
                        self.setMapPosition();
                    }, positionError => {
                        positionError;
                        self.setMapPosition();
                    });
                }
            },

            setMapPosition() {
                var options = { zoom: 11, disableDefaultUI: true };
                if (this.items.length === 0) {
                    options.center = this.browserLocation;
                }
                var div = document.getElementById("incident-map");
                this.map = new this.google.maps.Map(div, options);

                var bounds = new this.google.maps.LatLngBounds();
                for (var i = 0; i < this.computedItems.length; i++) {
                    var item = this.computedItems[i];
                    var position = new this.google.maps.LatLng(item.coordinates.latitude, item.coordinates.longitude);
                    var marker = new this.google.maps.Marker({ map: this.map, position: position });
                    var onMarkerClicked = ((m, d) => () => this.markerClicked(m, d))(marker, item);
                    this.google.maps.event.addListener(marker, 'click', onMarkerClicked);
                    bounds.extend(position);
                    this.items[i].marker = marker;
                    //markers.push(marker);
                }
                if (this.computedItems.length !== 0) {
                    var self = this;
                    this.google.maps.event.addListener(this.map, 'zoom_changed', () => {
                        var zoomChangeBoundsListener =
                            this.google.maps.event.addListener(self.map, 'bounds_changed', () => {
                                if (self.map.getZoom() > 11 && self.map.initialZoom == true) {
                                    // Change max/min zoom here
                                    self.map.setZoom(11);
                                    self.map.initialZoom = false;
                                }
                                this.google.maps.event.removeListener(zoomChangeBoundsListener);
                            });
                    });
                    this.map.initialZoom = true;
                    this.map.fitBounds(bounds);
                }
            },

            goToInspection(inspectionId) {
                this.$router.push({
                    name: 'inspection',
                    params: {
                        id: inspectionId
                    }
                });
            },

            rowClicked(item) {
                this.markerClicked(item.marker, item);
            },

            markerClicked(marker, incident) {
                if (this.infoWindow != null)
                    this.infoWindow.close();
                var html =
                    '<div id="content">' +
                    '<div id="siteNotice">' +
                    "</div>" +
                    '<h2 id="firstHeading" class="firstHeading mb-3">' + this.members[incident.memberId] + '</h2>' +
                    '<div id="bodyContent">' +
                    "<p><b>On: </b>" + this.formatDate(incident.on) + "</p>" +
                    "<p><b>Pest/Disease: </b>" + this.afflictions[incident.afflictionId] + "</p>" +
                    (incident.inspectionId != null ? "<p><b>Inspection: </b>" + this.inspections[incident.inspectionId] + "</p>" : "") +
                    "<p>" + incident.comments + "</p>" +
                    "</div>" +
                    "</div>";

                this.infoWindow = new this.google.maps.InfoWindow({ content: html });
                this.infoWindow.open(this.map, marker);
            },

            thisMonth() {
                var firstDayOfThisMonth = DateTime.local().startOf('month');
                var firstDayOfNextMonth = firstDayOfThisMonth.plus({ months: 1 });
                this.setDateRange(firstDayOfThisMonth, firstDayOfNextMonth);
                this.highlightButton = 1;
            },
            back() {
                var from = DateTime
                    .fromISO(this.from, { setZone: true })
                    .minus({ months: 1 });

                var to = DateTime
                    .fromISO(this.to, { setZone: true })
                    .minus({ months: 1 });

                this.setDateRange(from, to);
            },
            forward() {
                var from = DateTime
                    .fromISO(this.from, { setZone: true })
                    .plus({ months: 1 });

                var to = DateTime
                    .fromISO(this.to, { setZone: true })
                    .plus({ months: 1 });

                this.setDateRange(from, to);
            },
            async setDate(date) {
                this.updatingFilter = true;
                var dateIso = dateUtility.toDateIso(date);
                this.from = dateIso;
                this.to = dateIso;
                this.updateFilter();
                this.updatingFilter = false;
            },
            async setDateRange(from, to) {
                this.updatingFilter = true;
                var fromIso = dateUtility.toDateIso(from);
                this.from = fromIso;
                var toIso = dateUtility.toDateIso(to);
                this.to = toIso;
                this.updateFilter();
                this.updatingFilter = false;
            },
            updateFilter(forceValid, shiftChanged) {
                if (!shiftChanged) {
                    this.highlightButton = null;
                }
                if (typeof forceValid === 'undefined' || forceValid === null) {
                    forceValid = false;
                }

                if (!forceValid && !this.isFormValid) {
                    return;
                }

                this.mainFilter = { ...this.filter, from: this.from, to: this.to };
            },
            changeToSingleDateMode() {
                this.isDateRangeMode = false;
                this.to = this.from;
            },
            getColor(button) {
                if (this.highlightButton === button) {
                    return '#757575';
                }
                return '';
            },

            download() {
                if (!this.computedItems || this.computedItems.length === 0) {
                    this.notifyDialog = true;
                    return;
                }
                var items = this.computedItems.map(i => {
                    return {
                        'Id': i.id,
                        'On': i.on,
                        'Member Id': i.memberId,
                        'Member': this.members[i.memberId],
                        'Contact Id': i.contactId,
                        'Contact': this.contacts[i.contactId],
                        'Coordinates': i.coordinates,
                        'Pest/Disease Id': i.afflictionId,
                        'Pest/Disease': this.afflictions[i.afflictionId],
                        'Inspection Id': i.inspectionId,
                        'Inspection': this.inspections[i.inspectionId],
                        'Comments': i.comments
                    }
                });
                var title = "Biosecurity Incidents";

                exporter.downloadAsExcel({ title, items });
            },

            addItem() {
                this.editedItem = null;
                this.dialog = true;
            },

            editItem(item) {
                var editedItem = Object.assign({}, item);
                editedItem.marker = null;
                this.editedItem = editedItem;
                this.dialog = true;
            },

            async goToEditPage(item) {
                this.$router.push({
                    name: 'biosecurityIncident',
                    params: {
                        id: item.id
                    }
                });
            },

            onSave() {
                this.dialog = false;
                this.load();
            },

            onDelete(item) {
                var index = this.items.findIndex(i => i.id === item.id);

                if (index > -1) {
                    this.items.splice(index, 1);
                }
                this.dialog = false;
            },

            onCancel() {
                this.dialog = false;
            },

            canRead(claim) {
                return securityService.hasClaim(claim);
            },

            canEdit(claim) {
                return securityService.hasClaim(claim, true);
            },

            itemMatches(item, tokens) {
                for (var i = 0; i < tokens.length; i++) {
                    var token = tokens[i];
                    var isMatch =
                        this.includes(item.on, token) ||
                        this.includes(item.coordinates, token) ||
                        this.includes(item.comments, token) ||
                        this.includes(this.members[item.memberId], token) ||
                        this.includes(this.contacts[item.contactId], token) ||
                        this.includes(this.inspections[item.inspectionId], token) ||
                        this.includes(this.afflictions[item.afflictionId], token);

                    if (!isMatch) {
                        return false;
                    }
                }
                return true;
            },

            includes(value, token) {
                if (!value) {
                    return false;
                }

                return ('' + value).toLowerCase().includes(token);
            }
        },

        computed: {
            filteredHeaders() {
                var mainFilter = this.mainFilter;

                if (typeof mainFilter === 'undefined' || mainFilter === null) {
                    return this.headers;
                }

                return this.headers.filter(h => !mainFilter.hasOwnProperty(h.value));
            },

            computedItems() {
                var tokens = this.search.toLowerCase().trim().split(' ');
                if (tokens.length === 1 && tokens[0] === "") {
                    return this.items;
                }
                return this.items.filter(i => this.itemMatches(i, tokens));
            }
        },
        watch: {
            mainFilter() {
                this.load();
            },
            from() {
                if (!this.updatingFilter) {
                    this.updateFilter(true, false);
                }
            },
            to() {
                if (!this.updatingFilter) {
                    this.updateFilter(true, false);
                }
            },
            computedItems() {
                this.loadMap();
            }
        }
    }
</script>

<style scoped>
    .flex-scroll {
        flex-basis: 0px;
        flex-grow: 1;
        overflow-y: auto;
        min-width: 400px;
        max-width: 400px;
    }

    .row-extra-space {
        margin: 30px 0px 0px 0px;
    }

    .card-extra-space {
        margin: 4px 4px 4px 4px;
    }

    .title-remove-space {
        margin: -8px 0px 0px -8px;
    }

    .card-min-height >>> .v-data-table__mobile-row {
        min-height: 30px;
    }

    .hover-cell {
        cursor: pointer;
    }
</style>
